<template>
  <section>
    <input
      ref="mediaUploader"
      type="file"
      class="hidden"
      :multiple="allowMultiple"
      :accept="'video/quicktime,video/mp4,image/png,image/gif,image/jpeg,image/webp'"
      @change="processUpload"
      :disabled="remainingSlots <= 0"
    />
    <input type="hidden" name="file" />
    <div v-if="uploadStarted" class="pt-2">
      <ion-progress-bar :value="uploadProgress"></ion-progress-bar>
      <p class="text-tertiary">
        <ion-spinner class="align-middle" />Upload Progress:
        {{ uploadProgress }}%
      </p>
    </div>
    <ImageCropper
      v-if="enableCropping"
      ref="cropper"
      :aspectRatio="null"
      type="freeform"
      @croppedImage="handleCroppedImage"
    />
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useAuthStore } from "@/stores/auth";
import { apiBackendAuthAxios } from "@/axiosAuth";
import { IonProgressBar, IonSpinner } from "@ionic/vue";
import ImageCropper from "./ImageCropper.vue";

const triggers = defineEmits([
  "changedMedias",
  "uploadStarted",
  "uploadFinished",
]);
const mediaUploader = ref(null);
const uploadStarted = ref(false);
const uploadProgress = ref(0);
const cropper = ref(null);
const currentFile = ref(null);

const maxMedia = 5;

const props = defineProps({
  showPreview: {
    type: Boolean,
    default: true,
  },

  alreadyUploaded: {
    type: Number,
    default: 0,
  },

  allowMultiple: {
    type: Boolean,
    default: false,
  },

  enableCropping: {
    type: Boolean,
    default: false,
  },
});

const remainingSlots = computed(() => maxMedia - props.alreadyUploaded);

watch(uploadStarted, (newValue) => {
  if (newValue === true) {
    triggers("uploadStarted");
  } else {
    triggers("uploadFinished");
  }
});

function uploadExternalFile(file) {
  try {
    if (validateFile(file)) {
      if (
        props.enableCropping &&
        file.type.startsWith("image/") &&
        file.type !== "image/gif"
      ) {
        currentFile.value = file;
        showCropper(file);
      } else {
        uploadImageFile(file);
      }
    }
  } catch (error) {
    console.error("[MediaUploader] Error in uploadExternalFile:", error);
    resetCropperIfNeeded();
  }
}

function showCropper(file) {
  try {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (cropper.value) {
        cropper.value.setImageForCropping(e.target.result);
      }
    };
    reader.readAsDataURL(file);
  } catch (error) {
    console.error("[MediaUploader] Error in showCropper:", error);
    resetCropperIfNeeded();
  }
}

function handleCroppedImage(blob) {
  try {
    if (!blob || !currentFile.value) {
      // If blob is null or there's no current file, it means the crop was cancelled
      currentFile.value = null;
      return;
    }

    const fileName = currentFile.value.name;
    const file = new File([blob], fileName, { type: "image/jpeg" });

    uploadImageFile(file);
    currentFile.value = null;
  } catch (error) {
    console.error("[MediaUploader] Error in handleCroppedImage:", error);
    currentFile.value = null;
  }
}

function start() {
  try {
    if (remainingSlots.value > 0) {
      mediaUploader.value.click();
    } else {
      alert("Maximum upload limit reached.");
    }
  } catch (error) {
    console.error("Error in start:", error);
  }
}

function validateFile(file) {
  try {
    const validFileTypes = [
      "video/quicktime",
      "video/mp4",
      "image/png",
      "image/gif",
      "image/jpeg",
      "image/webp",
    ];

    if (!validFileTypes.includes(file.type)) {
      alert("Invalid file format");
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error in validateFile:", error);
    return false;
  }
}

const videoExtensions = [
  "mp4",
  "mov",
  "avi",
  "wmv",
  "flv",
  "mkv",
  "webm",
  "hevc",
  "heic",
  "heif",
];

const fileExtension = ref(null);

function processUpload(event) {
  try {
    if (
      !event.target ||
      !event.target.files ||
      event.target.files.length === 0
    ) {
      return;
    }

    Array.from(event.target.files).forEach((file) => {
      if (validateFile(file)) {
        fileExtension.value = file.name.split(".").pop().toLowerCase();

        const sanitizedFileName = file.name.replace(/[()\s]/g, "_");

        const sanitizedFile = new File([file], sanitizedFileName, {
          type: file.type,
        });

        if (
          props.enableCropping &&
          file.type.startsWith("image/") &&
          file.type !== "image/gif"
        ) {
          currentFile.value = sanitizedFile;
          showCropper(sanitizedFile);
        } else {
          uploadImageFile(sanitizedFile);
        }
      }
    });

    // Clear the input to allow selecting the same file again
    if (event.target) {
      event.target.value = null;
    }
  } catch (error) {
    console.error("Error in processUpload:", error);
    resetCropperIfNeeded();

    // Ensure we clear the input even if an error occurs
    if (event.target) {
      event.target.value = null;
    }
  }
}

const authStore = useAuthStore();

const uploadUrl =
  import.meta.env.VITE_API_HOST + "/v3/media/signed-storage-url";

async function uploadImageFile(file) {
  try {
    uploadStarted.value = true;

    let preview;

    await checkApi();

    if (videoExtensions.includes(fileExtension.value)) {
      const dimensions = await getVideoDimensions(file);
      preview = {
        url: "",
        isVideo: true,
        ...dimensions,
      };
    } else {
      const dimensions = await getImageDimensions(file);

      preview = {
        url: "",
        isVideo: false,
        ...dimensions,
      };
    }

    window.Vapor.store(file, {
      signedStorageUrl: uploadUrl,
      visibility: "public-read",
      headers: {
        Authorization: `Bearer ${authStore.accessToken}`,
      },
      progress: (progress) => {
        uploadProgress.value = Math.round(progress * 100);
      },
    })
      .then((response) => {
        const encodedFileName = encodeURIComponent(file.name);
        apiBackendAuthAxios
          .post("/media/upload", {
            uuid: response.uuid,
            key: response.key,
            bucket: response.bucket,
            name: encodedFileName,
            content_type: file.type,
          })
          .then((uploadResponse) => {
            if (remainingSlots.value > 0) {
              uploadProgress.value = 0;
              preview.url = uploadResponse.data.url;

              triggers("changedMedias", preview);
            } else {
              console.error("Upload limit reached");
            }
            uploadStarted.value = false;
          })
          .catch((error) => {
            console.error(error);
            uploadStarted.value = false;
          });
      })
      .catch((error) => {
        console.error("Vapor store error:", error);
        uploadStarted.value = false;
      });
  } catch (error) {
    console.error("Error uploading media", error, file);
    uploadStarted.value = false;
  }
}

function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        resolve({ widthPx: img.width, heightPx: img.height });
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function getVideoDimensions(file) {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";

    video.onloadedmetadata = function () {
      window.URL.revokeObjectURL(video.src);
      resolve({ widthPx: video.videoWidth, heightPx: video.videoHeight });
    };

    video.onerror = function () {
      reject("Error loading video metadata");
    };

    video.src = URL.createObjectURL(file);
  });
}

async function checkApi() {
  try {
    const { validateAccessToken, refreshAccessToken } = await import(
      "@/axiosAuth"
    );

    try {
      const isValid = await validateAccessToken(authStore.accessToken);
      if (!isValid) {
        await refreshAccessToken();
      }
    } catch (error) {
      console.error("API check failed:", error);
    }
  } catch (error) {
    console.error("Error importing from axiosAuth:", error);
  }
}

// Helper function to reset the cropper if needed
function resetCropperIfNeeded() {
  try {
    if (cropper.value && cropper.value.resetCropper) {
      cropper.value.resetCropper();
    }
    currentFile.value = null;
  } catch (error) {
    console.error("[MediaUploader] Error in resetCropperIfNeeded:", error);
  }
}

// Add this function to reset component state
function resetComponent() {
  try {
    uploadStarted.value = false;
    uploadProgress.value = 0;
    currentFile.value = null;

    // Reset the cropper component if it exists
    resetCropperIfNeeded();

    if (mediaUploader.value) {
      try {
        mediaUploader.value.value = "";
      } catch (e) {
        console.error(
          "[MediaUploader] Error clearing media uploader input:",
          e
        );
      }
    }
  } catch (error) {
    console.error("[MediaUploader] Error in resetComponent:", error);
  }
}

defineExpose({
  start,
  uploadExternalFile,
  uploadImageFile,
  resetComponent,
  resetCropperIfNeeded,
});

onBeforeUnmount(() => {
  try {
    // Reset all state when component is unmounted
    resetComponent();

    // Clean up event listeners if needed
    if (mediaUploader.value) {
      try {
        const input = mediaUploader.value;
        input.value = "";
      } catch (e) {
        console.error(
          "[MediaUploader] Error clearing media uploader input during unmount:",
          e
        );
      }
    }
  } catch (error) {
    console.error("[MediaUploader] Error in onBeforeUnmount:", error);
  }
});
</script>

<style scoped>
.text-tertiary {
  color: var(--tertiary-color);
}
</style>
